import network from '@/services/network';

/**
 * Admin-API für die softfair-Antrags-Defaults (BER-67).
 *
 * EINE Konfiguration je (Gesellschaft, Tarif, Antragstyp) — ein Tarif kann
 * mehrere Antragstypen mit eigenen Defaults bedienen (z. B. JustInvest als
 * Flex UND Kindersparen). Die Auswahllisten (Gesellschaften, Tarife, Fonds)
 * kommen live aus der softfair LV-API und sind kaskadierend: Gesellschaft →
 * Tarife → Fonds. Nur mit `softfair-defaults` (SA).
 */

/** Ein Fonds der Default-Auswahl (Anteil in Prozent, Summe = 100). */
export interface SoftfairFondsAuswahl {
  isin: string;
  name: string;
  anteil: number;
}

/** Eine Konfiguration (Gesellschaft, Tarif, Antragstyp) für die softfair-Antragserstellung. */
export interface SoftfairDefaults {
  id: number;
  gesellschaft_id: number;
  gesellschaft_name: string;
  tarif_id: string;
  tarif_name: string;
  /** Optionaler User-freundlicher Name für den Wizard (leer = Tarifname). */
  anzeigename: string | null;
  tarifvariante_id: string;
  /** Antragstyp der Konfiguration (application_types.value) — die Verknüpfung Tarif↔Typ. */
  application_type_value: string;
  /** Nur sichtbare Konfigurationen erscheinen im Wizard (Admin sieht alle). */
  sichtbar: boolean;
  hinterbliebene_ansparphase: string;
  hinterbliebene_rentenphase: string;
  rentengarantiedauer_jahre: number;
  /**
   * Dynamische Rentengarantie „bis Alter N" (z. B. Allianz 90 — druckt die
   * altersabhängige „N-fache Jahresrente" wie das Allianz-Portal); gesetzt
   * gewinnt sie über die feste Dauer, null = feste Dauer.
   */
  rentengarantie_bis_alter: number | null;
  beitragsdynamik_prozent: number;
  zahlweise: string;
  anlageart: string;
  fondsart: string;
  /**
   * Überschussverwendung Rentenphase (LV-API-Enum, z. B. „Teildynamisch" =
   * AXA „Erhöhte Startrente"); null = softfair-Default der Gesellschaft.
   */
  ueberschussverwendung_rentenphase: string | null;
  /**
   * Vermittlernummer beim Versicherer (je Gesellschaft fest, aus dem
   * Systemhandbuch; z. B. Continentale 6705083); null = keine mitsenden.
   */
  vermittlernummer_vu: string | null;
  /** Jährliche Fonds-Rückgewichtung (Tarifoption RebalancingJa). */
  rebalancing: boolean;
  /**
   * Begleitdokumente der Dokumentanforderung (Vorschlag/Angebot, Bedingungen,
   * Verbraucherinformationen) mit abholen und an das Antrags-PDF anhängen —
   * für Gesellschaften, die sie nicht selbst integrieren (Allianz).
   */
  begleitdokumente: boolean;
  fonds: SoftfairFondsAuswahl[];
}

export interface SoftfairGesellschaft {
  id: number;
  name: string;
  logo_url: string | null;
}

export interface SoftfairTarif {
  tarif_id: string;
  tarif_name: string;
  tarifvariante_id: string;
}

/** Eine Fondsliste der Tarifvariante inkl. Auswahlregeln. */
export interface SoftfairFondsliste {
  typ: string | null;
  max_anzahl: number | null;
  min_anteil_prozent: number | null;
  fonds: { isin: string; name: string }[];
}

/** Alle Konfigurationen (Übersicht). */
const list = async (): Promise<SoftfairDefaults[]> => {
  const { data } = await network.get<{ data: SoftfairDefaults[] }>('/softfair-defaults');
  return data.data;
};

/** Konfiguration einer Kombination — null, wenn noch nicht konfiguriert. */
const getTarif = async (
  gesellschaftId: number,
  tarifId: string,
  applicationTypeValue: string,
): Promise<SoftfairDefaults | null> => {
  const { data } = await network.get<{ data: SoftfairDefaults | null }>('/softfair-defaults/tarif', {
    params: {
      gesellschaft_id: gesellschaftId,
      tarif_id: tarifId,
      application_type_value: applicationTypeValue,
    },
  });
  return data.data;
};

/**
 * Konfiguration anlegen oder aktualisieren.
 *
 * Mit `id` wird genau diese Konfiguration aktualisiert; ohne `id` wird eine
 * NEUE Variante angelegt (mehrere je Gesellschaft/Tarif/Typ sind erlaubt).
 */
const upsert = async (
  defaults: Omit<SoftfairDefaults, 'id'> & { id?: number },
): Promise<SoftfairDefaults> => {
  const { data } = await network.put<{ data: SoftfairDefaults }>('/softfair-defaults', defaults);
  return data.data;
};

/** Sichtbarkeit einer Konfiguration umschalten (Toggle in der Übersicht). */
const setSichtbar = async (id: number, sichtbar: boolean): Promise<SoftfairDefaults> => {
  const { data } = await network.patch<{ data: SoftfairDefaults }>(
    `/softfair-defaults/${id}/sichtbar`,
    { sichtbar },
  );
  return data.data;
};

/** Konfiguration löschen (Kombination gilt danach als nicht konfiguriert). */
const remove = async (id: number): Promise<void> => {
  await network.delete(`/softfair-defaults/${id}`);
};

const gesellschaften = async (): Promise<SoftfairGesellschaft[]> => {
  const { data } = await network.get<{ data: SoftfairGesellschaft[] }>('/softfair-defaults/gesellschaften');
  return data.data;
};

const tarife = async (gesellschaftId: number): Promise<SoftfairTarif[]> => {
  const { data } = await network.get<{ data: SoftfairTarif[] }>('/softfair-defaults/tarife', {
    params: { gesellschaft_id: gesellschaftId },
  });
  return data.data;
};

const fonds = async (tarifvarianteId: string): Promise<SoftfairFondsliste[]> => {
  const { data } = await network.get<{ data: SoftfairFondsliste[] }>('/softfair-defaults/fonds', {
    params: { tarifvariante_id: tarifvarianteId },
  });
  return data.data;
};

const softfairAdmin = { list, getTarif, upsert, setSichtbar, remove, gesellschaften, tarife, fonds };

export default softfairAdmin;
